import { memo, useMemo, useState } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import { useTranslation } from 'react-i18next';

import { useKwhPerDayQuery } from '../../../hooks/useDashboardQueries';
import { ChartCard } from '../components/ChartCard';
import { MASK } from '../../../shared/utils/mask';

type DailyMetric = 'kwh' | 'idleRevenue';

interface DailyBarChartProps {
  dateRange: string;
  stationIds?: string;
  organizationIds?: string;
  /**
   * Which metrics get a tab, in order; the first is the one the card opens on.
   * Both series come from the same query, so a tab switch costs no request.
   */
  metrics?: DailyMetric[];
  /**
   * Hide mode: blanks the y-axis scale and the tooltip figures. The bars stay —
   * a shape is not a number, and an empty card would say nothing about the
   * product.
   */
  hidden?: boolean;
}

const DEFAULT_METRICS: DailyMetric[] = ['kwh', 'idleRevenue'];

interface DailyPoint {
  date: string;
  kwh: number;
  idleRevenue?: number;
}

const METRIC_CONFIG: Record<
  DailyMetric,
  {
    icon: string;
    titleKey: string;
    /** Short label for the tab strip; the title is too long to sit in a tab. */
    tabLabelKey: string;
    emptyKey: string;
    unitKey: string;
    seriesLabelKey: string;
    color: string;
    /** Tints the card's title icon to match the series. */
    accentClass: string;
    value: (p: DailyPoint) => number;
  }
> = {
  kwh: {
    icon: 'bolt',
    titleKey: 'dashboard.metrics.kwhPerDayTitle',
    tabLabelKey: 'dashboard.metrics.tabEnergy',
    emptyKey: 'dashboard.metrics.kwhPerDayEmpty',
    unitKey: 'dashboard.metrics.kwh',
    seriesLabelKey: 'dashboard.metrics.kwh',
    color: '#3b82f6',
    accentClass: 'is-energy',
    value: (p) => p.kwh,
  },
  idleRevenue: {
    icon: 'hourglass_empty',
    titleKey: 'dashboard.metrics.idleRevenuePerDayTitle',
    tabLabelKey: 'dashboard.metrics.tabIdle',
    emptyKey: 'dashboard.metrics.idleRevenuePerDayEmpty',
    unitKey: 'dashboard.metrics.uah',
    seriesLabelKey: 'dashboard.metrics.idleRevenuePerDayLabel',
    color: '#f59e0b',
    accentClass: 'is-idle',
    value: (p) => p.idleRevenue ?? 0,
  },
};

const formatDayLabel = (ymd: string): string => {
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return ymd;
  // Use a fixed 12:00 anchor to avoid the date drifting in Intl across DST.
  const anchor = new Date(y, m - 1, d, 12, 0, 0);
  return anchor.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

/**
 * Per-day bar chart over the kwh-per-day series, with a tab per metric.
 *
 * Delivered energy and idle revenue used to be two stacked cards, which put the
 * second one below the fold. They share one card now: the same query returns
 * both series, so switching tabs is free.
 */
export const DailyBarChart = memo(function DailyBarChart({
  dateRange,
  stationIds,
  organizationIds,
  metrics = DEFAULT_METRICS,
  hidden = false,
}: DailyBarChartProps) {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<DailyMetric>(metrics[0]);
  const config = METRIC_CONFIG[metric];
  const { data, isLoading } = useKwhPerDayQuery({ dateRange, stationIds, organizationIds });

  const { labels, values, hasData } = useMemo(() => {
    const series = data?.series ?? [];
    return {
      labels: series.map((p) => formatDayLabel(p.date)),
      values: series.map(config.value),
      hasData: series.some((p) => config.value(p) > 0),
    };
  }, [data, config]);

  return (
    <ChartCard
      title={t(config.titleKey)}
      icon={config.icon}
      // The card keeps one identity across tabs so its size does not jump;
      // only the title icon takes the active series' accent.
      className={`kwh-per-day-card ${config.accentClass}`}
      bodyClassName="kwh-per-day-body"
      isEmpty={!isLoading && !hasData}
      emptyMessage={t(config.emptyKey)}
      actions={
        metrics.length > 1 ? (
          <div className="chart-tabs" role="tablist">
            {metrics.map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={key === metric}
                className={`chart-tab${key === metric ? ' is-active' : ''}`}
                onClick={() => setMetric(key)}
              >
                {t(METRIC_CONFIG[key].tabLabelKey)}
              </button>
            ))}
          </div>
        ) : undefined
      }
    >
      <BarChart
        xAxis={[{ scaleType: 'band', data: labels }]}
        yAxis={[
          {
            id: metric,
            label: t(config.unitKey),
            position: 'left',
            // Blank the tick labels rather than the axis: the gridlines keep
            // the chart legible while the scale itself stays unreadable.
            valueFormatter: hidden ? () => '' : undefined,
          },
        ]}
        series={[
          {
            // Identify the series by metric. Without it the chart reuses
            // whichever series it registered first, so after a refetch the idle
            // tab rendered idle data on a UAH axis in the energy colour.
            id: metric,
            data: values,
            label: t(config.seriesLabelKey),
            color: config.color,
            yAxisId: metric,
            valueFormatter: (v) => {
              if (v == null) return '';
              if (hidden) return `${MASK} ${t(config.unitKey)}`;
              return `${v.toLocaleString('uk-UA', { maximumFractionDigits: 2 })} ${t(config.unitKey)}`;
            },
          },
        ]}
        height={260}
        margin={{ top: 16, bottom: 40, left: 56, right: 16 }}
        slotProps={{ legend: { hidden: true } }}
      />
    </ChartCard>
  );
});

export default DailyBarChart;
